import { API_BASE_URL } from "@kupikupi/brand";
import { useInfiniteQuery } from "@tanstack/react-query";
import { mergeOrderPages, queryOrders } from "../endpoints/order";
import { useBackendCurrency, useBackendLang } from "../lang-context";
import type { OrderListUrlInput } from "../path-adapter";

/**
 * Infinite ("load more") variant of `GET /order/listV2`.
 *
 * The backend paginates over **order items**, not orders, then collapses each
 * item-page into its distinct parent orders (see `mergeOrderPages` for the full
 * root-cause writeup of #442). A single fixed-size page therefore never shows
 * every order, and orders can straddle item-page boundaries. This hook fetches
 * successive item-pages and the consumer flattens+dedupes them with
 * `mergeOrderPages`, so every order is eventually visible.
 *
 * `getNextPageParam` advances while the backend reports more item-pages
 * (`pageNo < pages`). Auth required; pass `null` token to disable.
 *
 * Caller owns the `pageNo` (it is injected per fetch), so omit it from `input`.
 */
export function useOrdersInfinite(
  authToken: string | null,
  input: Omit<OrderListUrlInput, "pageNo"> = {},
) {
  const lang = useBackendLang();
  const currency = useBackendCurrency();
  return useInfiniteQuery({
    queryKey: ["orders", "infinite", input, lang, currency],
    enabled: !!authToken,
    queryFn: ({ pageParam }) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return queryOrders(
        { baseUrl: API_BASE_URL },
        authToken,
        {
          ...input,
          pageNo: pageParam,
        },
        { lang, currency },
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pageNo < lastPage.pages ? lastPage.pageNo + 1 : undefined,
    // Surface the flattened+deduped order list directly so consumers don't each
    // re-implement the merge. `data.orders` is the complete set fetched so far.
    select: (data) => ({ ...data, orders: mergeOrderPages(data.pages) }),
    staleTime: 30_000,
  });
}
